import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Ellipsis, X } from 'lucide-react';
import { useAuth } from '@/shared/auth/auth-provider';
import { useBrandingTheme } from '@/shared/hooks/use-branding-theme';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/shared/ui/dialog';
import { AppSidebar } from './app-sidebar';
import { AppTopbar } from './app-topbar';
import { MOBILE_PRIMARY, NAV_SECTIONS, type NavItem } from './navigation';

/**
 * The shell every signed-in screen renders inside.
 *
 * Desktop gets a persistent rail; phones get a bottom tab bar, because that
 * is where a thumb actually reaches on a handset held one-handed next to a
 * shelf. The four primary destinations are pinned and everything else lives
 * behind "More".
 */

function MobileNav() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const { primary, overflow } = useMemo(() => {
    const visible = NAV_SECTIONS.flatMap((section) => section.items).filter(
      (item) => !item.permission || can(item.permission),
    );

    const primaryItems = MOBILE_PRIMARY.map((to) =>
      visible.find((item) => item.to === to),
    ).filter((item): item is NavItem => Boolean(item));

    const primaryPaths = new Set(primaryItems.map((item) => item.to));
    const overflowItems = visible.filter((item) => !primaryPaths.has(item.to));

    return { primary: primaryItems, overflow: overflowItems };
  }, [can]);

  const isActive = (item: NavItem) =>
    pathname.startsWith(item.matchPrefix ?? item.to);

  return (
    <>
      <nav
        className="bg-background/95 supports-[backdrop-filter]:bg-background/85 fixed inset-x-0 bottom-0 z-40 border-t backdrop-blur lg:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        aria-label={t('nav.more')}
      >
        <ul className="flex items-stretch">
          {primary.map((item) => {
            const active = isActive(item);
            return (
              <li key={item.to} className="flex-1">
                <NavLink
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors',
                    active ? 'text-primary' : 'text-muted-foreground',
                  )}
                >
                  <item.icon className="size-5" aria-hidden />
                  <span className="truncate px-1">{t(item.labelKey)}</span>
                </NavLink>
              </li>
            );
          })}

          {overflow.length > 0 ? (
            <li className="flex-1">
              <button
                type="button"
                onClick={() => setMoreOpen(true)}
                className="text-muted-foreground flex w-full flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors"
              >
                <Ellipsis className="size-5" aria-hidden />
                <span className="truncate px-1">{t('nav.more')}</span>
              </button>
            </li>
          ) : null}
        </ul>
      </nav>

      <Dialog open={moreOpen} onOpenChange={setMoreOpen}>
        <DialogContent
          showCloseButton={false}
          className="top-auto bottom-0 left-0 max-h-[85dvh] w-full max-w-full translate-x-0 translate-y-0 gap-0 overflow-y-auto rounded-b-none rounded-t-2xl p-4 pb-8 data-[state=open]:animate-none data-[state=closed]:animate-none"
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">{t('nav.more')}</DialogTitle>

          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-semibold">{t('nav.more')}</p>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setMoreOpen(false)}
              aria-label={t('common.close')}
            >
              <X />
            </Button>
          </div>

          <div className="space-y-4">
            {NAV_SECTIONS.map((section) => {
              const items = section.items.filter(
                (item) =>
                  !MOBILE_PRIMARY.includes(item.to) &&
                  (!item.permission || can(item.permission)),
              );
              if (items.length === 0) return null;

              return (
                <div key={section.id} className="space-y-1">
                  {section.labelKey ? (
                    <p className="text-muted-foreground px-1 pb-1 text-[11px] font-medium tracking-wide uppercase">
                      {t(section.labelKey)}
                    </p>
                  ) : null}

                  {items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setMoreOpen(false)}
                      className="hover:bg-accent flex items-center gap-3 rounded-md px-2 py-2.5 text-sm font-medium transition-colors"
                    >
                      <item.icon className="size-4 shrink-0" aria-hidden />
                      <span className="truncate">{t(item.labelKey)}</span>
                    </NavLink>
                  ))}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function AppShell() {
  // The workspace's accent colour, applied to the design tokens. Mounted
  // here rather than in a provider because the setting is behind auth.
  useBrandingTheme();

  return (
    <div className="bg-background min-h-dvh">
      <div className="flex">
        <aside className="bg-sidebar sticky top-0 hidden h-dvh w-64 shrink-0 flex-col border-r lg:flex">
          <AppSidebar />
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <AppTopbar />
          <main className="flex-1 px-3 py-4 pb-28 lg:px-6 lg:py-6 lg:pb-8">
            <Outlet />
          </main>
        </div>
      </div>

      <MobileNav />
    </div>
  );
}
